import type { AssistantManualDraftOverlayItem } from '@prospero/shared/src/assistant/assistant-contract';
import {
  ASSISTANT_MANUAL_OVERLAY_MAX_SECTIONS,
  ASSISTANT_MANUAL_OVERLAY_SECTION_MAX_CHARS,
} from '@prospero/shared/src/assistant/assistant-contract';
import { createContext, type RefObject, useEffect } from 'react';

/**
 * Transport for the consultant's UNSAVED manual edits: the page registers a
 * collector that reads the dirty section editors at SEND time, and the panel
 * attaches the result to the outgoing message. The backend overlays it onto
 * the compiled manual state, so the copilot answers about the manual the user
 * is looking at — not the one last saved.
 */

export type ManualDraftOverlayCollector = () => AssistantManualDraftOverlayItem[];

/** Mutable holder: the page fills it, the panel reads it at send time. */
export type ManualDraftOverlayBox = RefObject<ManualDraftOverlayCollector | null>;

export const ManualDraftOverlayContext = createContext<ManualDraftOverlayBox | null>(null);

/** Registers `collector` into `box` for the lifetime of the calling component. */
export function useManualDraftOverlayRegistration(
  box: ManualDraftOverlayBox | null,
  collector: ManualDraftOverlayCollector | null,
): void {
  useEffect(() => {
    if (!box) {
      return;
    }
    box.current = collector;
    return () => {
      box.current = null;
    };
  }, [box, collector]);
}

export interface ManualDraftOverlayCollection {
  manualDraftOverlay?: AssistantManualDraftOverlayItem[];
  /**
   * Dirty sections seen but NOT sent because they exceed the transport caps
   * (max sections per message, max chars per section). The panel reports this
   * to the user — dropping edits silently would break the "context matches
   * what you see" promise, and sending them anyway would 400 the whole turn.
   */
  omittedCount: number;
}

/** Reads the box at send time and clamps it to the transport contract; absent box or collector means no overlay. */
export function collectManualDraftOverlay(
  box: ManualDraftOverlayBox | null,
): ManualDraftOverlayCollection {
  const collected = box?.current?.() ?? [];
  if (collected.length === 0) {
    return { omittedCount: 0 };
  }
  const withinCharCap = collected.filter(
    (item) => item.contentHtml.length <= ASSISTANT_MANUAL_OVERLAY_SECTION_MAX_CHARS,
  );
  const manualDraftOverlay = withinCharCap.slice(0, ASSISTANT_MANUAL_OVERLAY_MAX_SECTIONS);
  const omittedCount = collected.length - manualDraftOverlay.length;
  if (manualDraftOverlay.length === 0) {
    return { omittedCount };
  }
  return { manualDraftOverlay, omittedCount };
}
